import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Upload,
  FileText,
  CheckCircle,
  XCircle,
  Loader,
  AlertCircle,
  Trash2,
  RefreshCw,
  Clock,
  Link2,
  FileArchive,
  HardDrive
} from 'lucide-react';
import logger from '../../utils/logger';
import { useTranslation } from 'react-i18next';

const EmailImporter = ({ backendUrl, authToken, emailAccounts }) => {
  const { t } = useTranslation('admin');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importHistory, setImportHistory] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [currentImport, setCurrentImport] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    loadImportHistory();
    
    // Sélectionner le premier compte par défaut
    if (emailAccounts.length > 0 && !selectedAccount) {
      setSelectedAccount(emailAccounts[0].id);
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [emailAccounts]);

  const loadImportHistory = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/import/history`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setImportHistory(data);
        
        // Vérifier s'il y a un import en cours
        const processing = data.find(imp => imp.status === 'processing');
        if (processing) {
          setCurrentImport(processing);
          startPolling(processing.id);
        }
      }
    } catch (error) {
      logger.error('Error loading import history:', error);
    }
  };

  const startPolling = (importId) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    
    pollIntervalRef.current = setInterval(async () => {
      try {
        const response = await fetch(`${backendUrl}/api/admin/mail/import/status/${importId}`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        
        if (response.ok) {
          const status = await response.json();
          setCurrentImport(status);
          
          if (status.status === 'completed' || status.status === 'failed') {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            loadImportHistory();
            
            if (status.status === 'completed') {
              setSuccess(t('emailImport.importComplete', { imported: status.stats.imported, linked: status.stats.linked }));
            } else {
              setError(t('emailImport.importFailed', { error: status.error_message || t('emailImport.unknownError') }));
            }
          }
        }
      } catch (error) {
        logger.error('Error polling import status:', error);
      }
    }, 2000); // Poll toutes les 2 secondes
  };

  const handleFileSelect = async (file) => {
    if (!file) return;
    
    // Vérifier l'extension
    const filename = file.name.toLowerCase();
    if (!filename.endsWith('.mbox') && !filename.endsWith('.eml')) {
      setError(t('emailImport.unsupportedFormat'));
      return;
    }
    
    if (!selectedAccount) {
      setError(t('emailImport.selectAccountError'));
      return;
    }
    
    setError('');
    setSuccess('');
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('email_account_id', selectedAccount);
      
      // Upload avec XMLHttpRequest pour avoir la progression
      const xhr = new XMLHttpRequest();
      
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        }
      };
      
      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const response = JSON.parse(xhr.responseText);
          setSuccess(t('emailImport.uploaded'));
          setCurrentImport({ id: response.import_id, status: 'processing', progress: 0 });
          startPolling(response.import_id);
        } else {
          const errorData = JSON.parse(xhr.responseText);
          setError(errorData.detail || t('emailImport.uploadError'));
        }
        setUploading(false);
      };
      
      xhr.onerror = () => {
        setError(t('emailImport.networkError'));
        setUploading(false);
      };
      
      xhr.open('POST', `${backendUrl}/api/admin/mail/import/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
      xhr.send(formData);
      
    } catch (error) {
      logger.error('Error uploading file:', error);
      setError(t('emailImport.uploadFileError'));
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [selectedAccount]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const deleteImportRecord = async (importId) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/import/${importId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        loadImportHistory();
      }
    } catch (error) {
      logger.error('Error deleting import:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">{t('emailImport.statusPending')}</span>;
      case 'processing':
        return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 flex items-center"><Loader className="animate-spin mr-1" size={12} />{t('emailImport.statusProcessing')}</span>;
      case 'completed':
        return <span className="px-2 py-1 text-xs rounded-full bg-emerald-100 text-emerald-700">{t('emailImport.statusCompleted')}</span>;
      case 'failed':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">{t('emailImport.statusFailed')}</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-700">{status}</span>;
    }
  };

  const getAccountName = (accountId) => {
    const account = emailAccounts.find(a => a.id === accountId);
    return account ? account.email_address : accountId;
  };

  return (
    <div className="space-y-6">
      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center">
          <XCircle className="text-red-600 mr-3" size={20} />
          <span className="text-red-700">{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">
            <XCircle size={18} />
          </button>
        </div>
      )}
      
      {success && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center">
          <CheckCircle className="text-emerald-600 mr-3" size={20} />
          <span className="text-emerald-700">{success}</span>
          <button onClick={() => setSuccess('')} className="ml-auto text-emerald-400 hover:text-emerald-600">
            <XCircle size={18} />
          </button>
        </div>
      )}

      {/* Zone d'upload */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
          <Upload className="mr-2 text-indigo-600" size={24} />
          {t('emailImport.title')}
        </h3>
        
        <p className="text-sm text-slate-600 mb-4">
          {t('emailImport.desc')}
        </p>

        {/* Sélection du compte */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            {t('emailImport.accountLabel')}
          </label>
          <select
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">{t('emailImport.selectAccount')}</option>
            {emailAccounts.map(account => (
              <option key={account.id} value={account.id}>
                {account.email_address} ({account.display_name})
              </option>
            ))}
          </select>
        </div>

        {/* Zone de drop */}
        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
            isDragging 
              ? 'border-indigo-500 bg-indigo-50' 
              : 'border-slate-300 hover:border-indigo-400 hover:bg-slate-50'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept=".mbox,.eml"
            onChange={(e) => handleFileSelect(e.target.files[0])}
          />
          
          {uploading ? (
            <div className="space-y-4">
              <Loader className="mx-auto animate-spin text-indigo-600" size={48} />
              <div className="w-full max-w-xs mx-auto">
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-indigo-600 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-sm text-slate-600 mt-2">{t('emailImport.uploading', { progress: uploadProgress })}</p>
              </div>
            </div>
          ) : currentImport && currentImport.status === 'processing' ? (
            <div className="space-y-4">
              <Loader className="mx-auto animate-spin text-indigo-600" size={48} />
              <div className="w-full max-w-xs mx-auto">
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-indigo-600 transition-all duration-300"
                    style={{ width: `${currentImport.progress || 0}%` }}
                  />
                </div>
                <p className="text-sm text-slate-600 mt-2">
                  {t('emailImport.processing', { progress: currentImport.progress || 0 })}
                </p>
                {currentImport.stats && (
                  <p className="text-xs text-slate-500 mt-1">
                    {t('emailImport.statsLine', { imported: currentImport.stats.imported, duplicates: currentImport.stats.duplicates, linked: currentImport.stats.linked })}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <>
              <FileArchive className="mx-auto text-slate-400 mb-4" size={48} />
              <p className="text-slate-600 font-medium">
                {t('emailImport.dropHere')}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                {t('emailImport.orClick')}
              </p>
              <p className="text-xs text-slate-400 mt-2">
                {t('emailImport.supportedFormats')}
              </p>
            </>
          )}
        </div>

        {/* Info sur les formats */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="font-medium text-blue-800">📧 Gmail</p>
            <p className="text-blue-600 text-xs">{t('emailImport.gmailHint')}</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-3">
            <p className="font-medium text-purple-800">📮 Outlook</p>
            <p className="text-purple-600 text-xs">{t('emailImport.outlookHint')}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="font-medium text-slate-800">🍎 Apple Mail</p>
            <p className="text-slate-600 text-xs">{t('emailImport.appleHint')}</p>
          </div>
        </div>
      </div>

      {/* Historique des imports */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900 flex items-center">
            <Clock className="mr-2 text-slate-600" size={24} />
            {t('emailImport.historyTitle')}
          </h3>
          <button
            onClick={loadImportHistory}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            title={t('emailImport.refresh')}
          >
            <RefreshCw size={18} />
          </button>
        </div>

        {importHistory.length === 0 ? (
          <p className="text-slate-500 text-center py-8">
            {t('emailImport.noImports')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">{t('emailImport.colFile')}</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">{t('emailImport.colAccount')}</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">{t('emailImport.colStatus')}</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">{t('emailImport.colResult')}</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">{t('emailImport.colDate')}</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">{t('emailImport.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {importHistory.map((imp) => (
                  <tr key={imp.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <FileText className="text-slate-400 mr-2" size={16} />
                        <div>
                          <p className="text-sm font-medium text-slate-900">{imp.filename}</p>
                          <p className="text-xs text-slate-500">{formatFileSize(imp.file_size)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">
                      {getAccountName(imp.email_account_id)}
                    </td>
                    <td className="py-3 px-4">
                      {getStatusBadge(imp.status)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {imp.stats && (
                        <div className="text-xs">
                          <span className="text-emerald-600 font-medium">{imp.stats.imported}</span>
                          <span className="text-slate-400"> {t('emailImport.imported')}</span>
                          {imp.stats.linked > 0 && (
                            <>
                              <span className="text-slate-300 mx-1">•</span>
                              <span className="text-blue-600 font-medium">{imp.stats.linked}</span>
                              <span className="text-slate-400"> {t('emailImport.linked')}</span>
                            </>
                          )}
                          {imp.stats.duplicates > 0 && (
                            <>
                              <span className="text-slate-300 mx-1">•</span>
                              <span className="text-amber-600">{imp.stats.duplicates} {t('emailImport.duplicates')}</span>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-500">
                      {formatDate(imp.created_at)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => deleteImportRecord(imp.id)}
                        className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title={t('emailImport.deleteFromHistory')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailImporter;
